import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Mic, MicOff, Loader2, Save, ArrowLeft, Youtube, Instagram, Twitter, Smartphone, Upload, ImageIcon, PenLine, Calendar, Clock, AlertTriangle, CreditCard, CheckCircle, Link2, Unlink, HardDriveDownload } from 'lucide-react';
import { toast } from 'sonner';

const tones = [
  { value: 'fun', label: 'Fun et energique' },
  { value: 'serieux', label: 'Serieux et professionnel' },
  { value: 'conspi', label: 'Conspirationnel' },
  { value: 'motivant', label: 'Motivant' },
  { value: 'educatif', label: 'Educatif' },
];

const languages = [
  { value: 'en', label: 'English' },
  { value: 'fr', label: 'Francais' },
  { value: 'ar', label: 'Arabic / العربية' },
  { value: 'de', label: 'Deutsch' },
  { value: 'es', label: 'Espanol (Espana)' },
  { value: 'es-la', label: 'Espanol (Latinoamerica)' },
  { value: 'it', label: 'Italiano' },
  { value: 'ja', label: '日本語 (Japanese)' },
  { value: 'ko', label: '한국어 (Korean)' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'pt', label: 'Portugues (Portugal)' },
  { value: 'pt-br', label: 'Portugues (Brasil)' },
  { value: 'ru', label: 'Русский (Russian)' },
  { value: 'zh', label: '中文 (Chinese)' },
];

export default function ProjectCreatePage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { user } = useAuth();

  const [form, setForm] = useState({
    name: '', description: '', niche: '', language: 'en', tone: 'fun',
    frequency_per_week: 1, transcribed_description: '', written_description: '',
    project_image: '',
    schedule_enabled: false, schedule_days: [], schedule_time: '10:00',
    youtube_credentials: '', instagram_token: '',
    x_api_key: '', x_api_secret: '', x_access_token: '', x_access_secret: '',
    tiktok_enabled: false,
    linked_youtube_accounts: [],
    auto_delete_after_publish: true,
  });
  const [saving, setSaving] = useState(false);
  const [loadingProject, setLoadingProject] = useState(isEdit);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [youtubeStatus, setYoutubeStatus] = useState({ connected: false, channel_name: null });
  const [availableYtAccounts, setAvailableYtAccounts] = useState([]);

  // Voice recording
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  useEffect(() => {
    loadAvailableYtAccounts();
    if (isEdit) {
      loadProject();
      loadYoutubeStatus();
    }
    // Check YouTube OAuth callback
    const ytStatus = searchParams.get('youtube');
    const channel = searchParams.get('channel');
    if (ytStatus === 'success') {
      toast.success(`YouTube connecte : ${channel || 'Chaine'}`);
      loadYoutubeStatus();
      loadAvailableYtAccounts();
    } else if (ytStatus === 'error') {
      toast.error('Erreur de connexion YouTube');
    }
  }, [id]);

  const loadAvailableYtAccounts = async () => {
    try {
      const res = await api.get('/social-accounts?platform=youtube');
      setAvailableYtAccounts(res.data.accounts || []);
    } catch {
      // Silent: account list is non-critical (UI degrades gracefully)
    }
  };

  const loadProject = async () => {
    try {
      const res = await api.get(`/projects/${id}`);
      const p = res.data;
      setForm({
        name: p.name || '', description: p.description || '', niche: p.niche || '',
        language: p.language || 'fr', tone: p.tone || 'fun',
        frequency_per_week: p.frequency_per_week || 1,
        transcribed_description: p.transcribed_description || '',
        written_description: p.written_description || '',
        project_image: p.project_image || '',
        schedule_enabled: p.schedule_enabled || false,
        schedule_days: p.schedule_days || [],
        schedule_time: p.schedule_time || '10:00',
        youtube_credentials: '', instagram_token: '',
        x_api_key: '', x_api_secret: '', x_access_token: '', x_access_secret: '',
        tiktok_enabled: p.tiktok_enabled || false,
        linked_youtube_accounts: p.linked_youtube_accounts || [],
        auto_delete_after_publish: p.auto_delete_after_publish !== false,
      });
      if (p.project_image) setImagePreview('uploaded');
    } catch {
      toast.error('Projet introuvable');
      navigate('/dashboard');
    } finally {
      setLoadingProject(false);
    }
  };

  const loadYoutubeStatus = async () => {
    if (!id) return;
    try {
      const res = await api.get(`/projects/${id}/youtube-status`);
      setYoutubeStatus(res.data);
    } catch {
      // Silent: youtube status is non-blocking; user can still edit project
    }
  };

  const handleYoutubeConnect = async () => {
    try {
      const res = await api.get(`/youtube/connect?project_id=${id}`);
      if (res.data.auth_url) {
        window.location.href = res.data.auth_url;
      }
    } catch (err) {
      toast.error('Erreur de connexion YouTube');
    }
  };

  const handleYoutubeDisconnect = async () => {
    try {
      await api.post(`/projects/${id}/youtube-disconnect`);
      setYoutubeStatus({ connected: false, channel_name: null });
      toast.success('YouTube deconnecte');
    } catch {
      toast.error('Erreur');
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => audioChunksRef.current.push(e.data);
      mediaRecorder.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await transcribeAudio(blob);
      };
      mediaRecorder.start();
      setIsRecording(true);
    } catch {
      toast.error("Impossible d'acceder au micro. Verifiez les permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(t => t.stop());
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (blob) => {
    setIsTranscribing(true);
    const formData = new FormData();
    formData.append('audio', blob, 'voice_input.webm');
    try {
      const res = await api.post('/transcribe', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.transcribed_text) {
        handleChange('transcribed_description', res.data.transcribed_text);
        // Auto-extract niche from transcribed text
        const keywords = ['crypto', 'finance', 'sport', 'sante', 'politique', 'people', 'tech', 'gaming', 'cuisine', 'mode'];
        for (const kw of keywords) {
          if (res.data.transcribed_text.toLowerCase().includes(kw)) {
            handleChange('niche', kw);
            break;
          }
        }
        toast.success('Transcription terminee !');
      }
    } catch (err) {
      toast.error('Erreur lors de la transcription');
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image trop volumineuse (max 10 MB)');
      return;
    }
    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      handleChange('project_image', res.data.path);
      setImagePreview(URL.createObjectURL(file));
      toast.success('Image chargee !');
    } catch (err) {
      toast.error('Erreur lors du chargement de l\'image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Le nom du projet est requis');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Remove empty credential fields to avoid overwriting
      for (const key of ['youtube_credentials', 'instagram_token', 'x_api_key', 'x_api_secret', 'x_access_token', 'x_access_secret']) {
        if (!payload[key]) delete payload[key];
      }
      if (isEdit) {
        await api.put(`/projects/${id}`, payload);
        toast.success('Projet mis a jour !');
      } else {
        await api.post('/projects', payload);
        toast.success('Projet cree avec succes !');
      }
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loadingProject) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="project-create-page">
      <button
        onClick={() => navigate('/dashboard')}
        className="flex items-center gap-2 text-sm text-white/40 hover:text-white mb-6 transition-colors"
        data-testid="back-to-dashboard"
      >
        <ArrowLeft className="w-4 h-4" /> Retour au dashboard
      </button>

      <h1 className="font-heading text-2xl sm:text-3xl font-medium text-white tracking-tight mb-8">
        {isEdit ? 'Modifier le projet' : 'Creer un nouveau projet'}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Voice input */}
        <div className="card-surface rounded-lg p-8" data-testid="voice-input-section">
          <h2 className="font-heading text-lg font-medium text-white mb-2">Entree vocale</h2>
          <p className="text-sm text-white/40 mb-6">Decrivez votre projet a la voix. L'IA transcrira et remplira les champs.</p>

          <button
            type="button"
            onClick={isRecording ? stopRecording : startRecording}
            disabled={isTranscribing}
            className={`w-full h-20 rounded-xl flex items-center justify-center gap-3 text-base font-medium transition-all ${
              isRecording
                ? 'bg-red-600/20 border border-red-500/30 text-red-400'
                : 'bg-white/5 border border-white/10 text-white hover:bg-white/10'
            } ${isTranscribing ? 'opacity-50 cursor-not-allowed' : ''}`}
            data-testid="voice-record-btn"
          >
            {isTranscribing ? (
              <><Loader2 className="w-6 h-6 animate-spin" /> Transcription avec Whisper...</>
            ) : isRecording ? (
              <><div className="recording-pulse"><MicOff className="w-6 h-6" /></div> Arreter l'enregistrement</>
            ) : (
              <><Mic className="w-6 h-6" /> Parler pour decrire mon projet</>
            )}
          </button>

          {form.transcribed_description && (
            <div className="mt-4">
              <label className="block text-xs text-white/40 mb-2">Texte transcrit (modifiable)</label>
              <textarea
                value={form.transcribed_description}
                onChange={(e) => handleChange('transcribed_description', e.target.value)}
                className="w-full h-24 px-4 py-3 input-dark rounded-lg text-sm resize-none"
                data-testid="transcribed-text"
              />
            </div>
          )}
        </div>

        {/* Written description (accessibility) */}
        <div className="card-surface rounded-lg p-8" data-testid="written-description-section">
          <div className="flex items-center gap-2 mb-2">
            <PenLine className="w-5 h-5 text-white/60" />
            <h2 className="font-heading text-lg font-medium text-white">Description ecrite</h2>
          </div>
          <p className="text-sm text-white/40 mb-4">Decrivez votre projet par ecrit. Indiquez votre niche, votre audience cible, le style souhaite...</p>
          <textarea
            value={form.written_description}
            onChange={(e) => handleChange('written_description', e.target.value)}
            className="w-full h-32 px-4 py-3 input-dark rounded-lg text-sm resize-none"
            placeholder="Exemple : Je veux une chaine sur les actualites crypto en francais, avec un ton fun et energique, ciblant les 18-35 ans..."
            data-testid="written-description-input"
          />
        </div>

        {/* Image upload */}
        <div className="card-surface rounded-lg p-8" data-testid="image-upload-section">
          <div className="flex items-center gap-2 mb-2">
            <ImageIcon className="w-5 h-5 text-white/60" />
            <h2 className="font-heading text-lg font-medium text-white">Photo / Avatar du projet</h2>
          </div>
          <p className="text-sm text-white/40 mb-4">Chargez une image qui represente votre projet (votre photo, un logo, etc.). Elle sera utilisee dans les videos generees.</p>
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
              {imagePreview ? (
                imagePreview === 'uploaded' ? (
                  <ImageIcon className="w-8 h-8 text-[#34C759]" />
                ) : (
                  <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                )
              ) : (
                <ImageIcon className="w-8 h-8 text-white/20" />
              )}
            </div>
            <div className="flex-1">
              <label
                className={`inline-flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-medium cursor-pointer transition-all ${
                  uploadingImage ? 'bg-white/5 text-white/30' : 'bg-white/5 border border-white/10 text-white hover:bg-white/10'
                }`}
              >
                {uploadingImage ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Chargement...</>
                ) : (
                  <><Upload className="w-4 h-4" /> Choisir une image</>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                  className="hidden"
                  data-testid="image-upload-input"
                />
              </label>
              <p className="text-xs text-white/20 mt-2">JPG, PNG, WebP. Max 10 MB.</p>
              {form.project_image && <p className="text-xs text-[#34C759] mt-1">Image chargee avec succes</p>}
            </div>
          </div>
        </div>

        {/* Project details */}
        <div className="card-surface rounded-lg p-8 space-y-5" data-testid="project-details-section">
          <h2 className="font-heading text-lg font-medium text-white mb-2">Details du projet</h2>

          <div>
            <label className="block text-xs text-white/40 mb-2">Nom de la chaine *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className="w-full px-4 py-3 input-dark rounded-lg text-sm"
              placeholder="Crypto News FR"
              required
              data-testid="project-name-input"
            />
          </div>

          <div>
            <label className="block text-xs text-white/40 mb-2">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className="w-full h-20 px-4 py-3 input-dark rounded-lg text-sm resize-none"
              placeholder="Decrivez votre projet en quelques mots..."
              data-testid="project-description-input"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs text-white/40 mb-2">Niche / Theme</label>
              <input
                type="text"
                value={form.niche}
                onChange={(e) => handleChange('niche', e.target.value)}
                className="w-full px-4 py-3 input-dark rounded-lg text-sm"
                placeholder="cryptocurrency, sport, tech..."
                data-testid="project-niche-input"
              />
            </div>
            <div>
              <label className="block text-xs text-white/40 mb-2">Langue</label>
              <select
                value={form.language}
                onChange={(e) => handleChange('language', e.target.value)}
                className="w-full px-4 py-3 input-dark rounded-lg text-sm"
                data-testid="project-language-select"
              >
                {languages.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs text-white/40 mb-2">Ton souhaite</label>
              <select
                value={form.tone}
                onChange={(e) => handleChange('tone', e.target.value)}
                className="w-full px-4 py-3 input-dark rounded-lg text-sm"
                data-testid="project-tone-select"
              >
                {tones.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-white/40 mb-2">Frequence (videos/semaine)</label>
              <input
                type="number"
                min={1}
                max={21}
                value={form.frequency_per_week}
                onChange={(e) => handleChange('frequency_per_week', parseInt(e.target.value) || 1)}
                className="w-full px-4 py-3 input-dark rounded-lg text-sm"
                data-testid="project-frequency-input"
              />
            </div>
          </div>
        </div>

        {/* Schedule / Planning */}
        <div className="card-surface rounded-lg p-8 space-y-5" data-testid="schedule-section">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-5 h-5 text-[#FF3300]" />
            <h2 className="font-heading text-lg font-medium text-white">Planning de diffusion</h2>
          </div>
          <p className="text-sm text-white/40">Choisissez les jours et l'heure de diffusion automatique. Chaque diffusion consomme 100 credits.</p>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => handleChange('schedule_enabled', !form.schedule_enabled)}
              className={`w-12 h-7 rounded-full transition-colors relative ${form.schedule_enabled ? 'bg-[#FF3300]' : 'bg-white/10'}`}
              data-testid="schedule-toggle"
            >
              <div className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${form.schedule_enabled ? 'left-6' : 'left-1'}`} />
            </button>
            <span className="text-sm text-white">{form.schedule_enabled ? 'Planning actif' : 'Planning desactive'}</span>
          </div>

          {form.schedule_enabled && (
            <div className="space-y-5 mt-4">
              <div>
                <label className="block text-xs text-white/40 mb-3">Jours de diffusion</label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: 'monday', label: 'Lun' }, { id: 'tuesday', label: 'Mar' },
                    { id: 'wednesday', label: 'Mer' }, { id: 'thursday', label: 'Jeu' },
                    { id: 'friday', label: 'Ven' }, { id: 'saturday', label: 'Sam' },
                    { id: 'sunday', label: 'Dim' },
                  ].map(day => {
                    const isSelected = form.schedule_days.includes(day.id);
                    return (
                      <button
                        key={day.id}
                        type="button"
                        onClick={() => {
                          const newDays = isSelected
                            ? form.schedule_days.filter(d => d !== day.id)
                            : [...form.schedule_days, day.id];
                          handleChange('schedule_days', newDays);
                          handleChange('frequency_per_week', newDays.length);
                        }}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          isSelected
                            ? 'bg-[#FF3300] text-white'
                            : 'bg-white/5 text-white/40 border border-white/10 hover:bg-white/10'
                        }`}
                        data-testid={`schedule-day-${day.id}`}
                      >
                        {day.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="flex items-center gap-2 text-xs text-white/40 mb-2">
                    <Clock className="w-3 h-3" /> Heure de diffusion
                  </label>
                  <input
                    type="time"
                    value={form.schedule_time}
                    onChange={(e) => handleChange('schedule_time', e.target.value)}
                    className="w-full px-4 py-3 input-dark rounded-lg text-sm"
                    data-testid="schedule-time-input"
                  />
                </div>
                <div className="flex flex-col justify-end">
                  <div className="bg-white/[0.03] rounded-lg p-4 border border-white/5">
                    <p className="text-xs text-white/40">Cout hebdomadaire</p>
                    <p className="text-lg font-heading font-medium text-white mt-1">
                      {form.schedule_days.length * 100} <span className="text-xs text-white/40">credits/semaine</span>
                    </p>
                    <p className="text-xs text-white/30 mt-1">{form.schedule_days.length} diffusion(s)/semaine</p>
                  </div>
                </div>
              </div>

              {user && user.plan_type !== 'unlimited' && form.schedule_days.length > 0 && (user?.credits_balance || 0) < form.schedule_days.length * 100 && (
                <div className="flex items-center gap-3 p-4 rounded-lg bg-[#FF3B30]/10 border border-[#FF3B30]/20" data-testid="credits-warning">
                  <AlertTriangle className="w-5 h-5 text-[#FF3B30] flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-white">Credits insuffisants pour ce planning</p>
                    <p className="text-xs text-white/40 mt-0.5">
                      Vous avez {user?.credits_balance || 0} credits, il en faut {form.schedule_days.length * 100}/semaine.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate('/pricing')}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#FF3300] text-white text-xs font-medium hover:bg-[#FF3300]/80 transition-all whitespace-nowrap"
                    data-testid="recharge-credits-btn"
                  >
                    <CreditCard className="w-3 h-3" /> Recharger
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* API Keys */}
        <div className="card-surface rounded-lg p-8 space-y-5" data-testid="api-keys-section">
          <div>
            <h2 className="font-heading text-lg font-medium text-white mb-1">Cles API de publication</h2>
            <p className="text-xs text-white/30">Stockees chiffrees. Necessaires pour la publication automatique.</p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="flex items-center gap-2 text-xs text-white/40 mb-2">
                <Youtube className="w-4 h-4 text-red-500" /> Comptes YouTube a publier
              </label>
              {availableYtAccounts.length > 0 ? (
                <div className="space-y-2" data-testid="yt-accounts-select">
                  {availableYtAccounts.map((acc) => {
                    const checked = (form.linked_youtube_accounts || []).includes(acc.id);
                    return (
                      <label
                        key={acc.id}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                          checked ? 'bg-red-600/10 border border-red-500/30' : 'bg-white/[0.03] border border-white/5 hover:bg-white/[0.05]'
                        }`}
                        data-testid={`yt-account-${acc.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const arr = new Set(form.linked_youtube_accounts || []);
                            if (e.target.checked) arr.add(acc.id); else arr.delete(acc.id);
                            handleChange('linked_youtube_accounts', Array.from(arr));
                          }}
                          className="w-4 h-4 accent-red-500"
                        />
                        {acc.picture ? (
                          <img src={acc.picture} alt="" className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                            <Youtube className="w-4 h-4 text-red-500" />
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="text-sm text-white">{acc.account_name}</p>
                          <p className="text-xs text-white/30">Connecte</p>
                        </div>
                        {checked && <CheckCircle className="w-4 h-4 text-[#34C759]" />}
                      </label>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => navigate('/connections')}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-white/[0.03] border border-dashed border-white/10 text-white/50 hover:text-white hover:bg-white/[0.06] transition-all text-xs"
                    data-testid="manage-connections-btn"
                  >
                    <Link2 className="w-3.5 h-3.5" /> Gerer mes comptes connectes
                  </button>
                </div>
              ) : (
                <div className="p-4 rounded-lg bg-white/[0.02] border border-white/5">
                  <p className="text-xs text-white/40 mb-2">Aucun compte YouTube connecte.</p>
                  <button
                    type="button"
                    onClick={() => navigate('/connections')}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600/10 border border-red-500/20 text-red-400 hover:bg-red-600/20 transition-all text-xs font-medium"
                    data-testid="go-to-connections-btn"
                  >
                    <Link2 className="w-3.5 h-3.5" /> Connecter un compte YouTube
                  </button>
                </div>
              )}
            </div>

            <div>
              <label className="flex items-center gap-2 text-xs text-white/40 mb-2">
                <Instagram className="w-4 h-4 text-pink-500" /> Instagram / Facebook Access Token
              </label>
              <input
                type="password"
                value={form.instagram_token}
                onChange={(e) => handleChange('instagram_token', e.target.value)}
                className="w-full px-4 py-3 input-dark rounded-lg text-sm"
                placeholder={isEdit ? '***configure*** (laisser vide pour ne pas modifier)' : 'EAA... (long-lived token)'}
                data-testid="api-instagram-input"
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-xs text-white/40 mb-2">
                <Twitter className="w-4 h-4 text-blue-400" /> X (Twitter) Credentials
              </label>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="password"
                  value={form.x_api_key}
                  onChange={(e) => handleChange('x_api_key', e.target.value)}
                  className="px-4 py-3 input-dark rounded-lg text-sm"
                  placeholder="API Key"
                  data-testid="api-x-key-input"
                />
                <input
                  type="password"
                  value={form.x_api_secret}
                  onChange={(e) => handleChange('x_api_secret', e.target.value)}
                  className="px-4 py-3 input-dark rounded-lg text-sm"
                  placeholder="API Secret"
                  data-testid="api-x-secret-input"
                />
                <input
                  type="password"
                  value={form.x_access_token}
                  onChange={(e) => handleChange('x_access_token', e.target.value)}
                  className="px-4 py-3 input-dark rounded-lg text-sm"
                  placeholder="Access Token"
                  data-testid="api-x-access-input"
                />
                <input
                  type="password"
                  value={form.x_access_secret}
                  onChange={(e) => handleChange('x_access_secret', e.target.value)}
                  className="px-4 py-3 input-dark rounded-lg text-sm"
                  placeholder="Access Secret"
                  data-testid="api-x-access-secret-input"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => handleChange('tiktok_enabled', !form.tiktok_enabled)}
                className={`w-10 h-6 rounded-full transition-colors relative ${
                  form.tiktok_enabled ? 'bg-[#FF3300]' : 'bg-white/10'
                }`}
                data-testid="tiktok-toggle"
              >
                <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                  form.tiktok_enabled ? 'left-5' : 'left-1'
                }`} />
              </button>
              <label className="flex items-center gap-2 text-sm text-white/60">
                <Smartphone className="w-4 h-4" /> Activer publication TikTok
              </label>
            </div>
          </div>
        </div>

        {/* Storage settings */}
        <div className="card-surface rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-3">
            <HardDriveDownload className="w-5 h-5 text-[#FF3300]" />
            <h2 className="font-heading text-lg font-medium text-white">Gestion du stockage</h2>
          </div>
          <div className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.02]">
            <button
              type="button"
              onClick={() => handleChange('auto_delete_after_publish', !form.auto_delete_after_publish)}
              className={`w-10 h-6 rounded-full transition-colors relative flex-shrink-0 mt-1 ${
                form.auto_delete_after_publish ? 'bg-[#FF3300]' : 'bg-white/10'
              }`}
              data-testid="auto-delete-toggle"
            >
              <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                form.auto_delete_after_publish ? 'left-5' : 'left-1'
              }`} />
            </button>
            <div>
              <p className="text-sm text-white font-medium">Auto-suppression apres publication</p>
              <p className="text-xs text-white/40 mt-1 leading-relaxed">
                Supprime automatiquement le fichier video 7 jours apres publication pour liberer l'espace. L'historique (script, titre, lien YouTube) est <span className="text-white/70">toujours conserve</span>. Recommande.
              </p>
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="btn-outline text-sm"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-accent text-sm px-8 py-3 flex items-center gap-2 disabled:opacity-50"
            data-testid="save-project-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isEdit ? 'Mettre a jour' : 'Creer le projet'}
          </button>
        </div>
      </form>
    </div>
  );
}
